import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { whatsappService } from '../services/whatsappService';
import { useCurrentUserId } from '../hooks/useCurrentUserId';

interface PhoneNumber {
  id: string;
  displayPhoneNumber: string;
  verifiedName: string;
  qualityRating: string;
}

interface Waba {
  id: string;
  name: string;
  timezoneId: string;
  hasPhoneNumbers: boolean;
  phoneNumbers: PhoneNumber[];
}

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: ${props => props.theme.colors.background};
`;

const Card = styled.div`
  max-width: 800px;
  width: 100%;
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 10px 25px rgba(0,0,0,0.15);
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 1.5rem;
  font-size: 1rem;
`;

const ErrorBox = styled.div`
  background: rgba(255, 59, 48, 0.1);
  border: 1px solid rgba(255, 59, 48, 0.3);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const ErrorText = styled.p`
  color: #ff3b30;
  font-size: 0.875rem;
`;

const WabaCard = styled.div<{ selected: boolean }>`
  border: 2px solid ${props => props.selected ? '#ff3b30' : props.theme.colors.border};
  background: ${props => props.selected ? 'rgba(255, 59, 48, 0.05)' : props.theme.colors.backgroundSecondary};
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${props => props.selected ? '#ff3b30' : 'rgba(255, 59, 48, 0.5)'};
  }
`;

const WabaHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 0.5rem;
`;

const WabaName = styled.h3`
  font-weight: 600;
  color: ${props => props.theme.colors.textPrimary};
  font-size: 1.125rem;
`;

const WabaId = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const Badge = styled.span<{ variant: 'success' | 'warning' }>`
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => props.variant === 'success'
    ? 'rgba(52, 199, 89, 0.2)'
    : 'rgba(255, 204, 0, 0.2)'};
  color: ${props => props.variant === 'success' ? '#34c759' : '#ffcc00'};
`;

const PhoneCard = styled.div<{ selected: boolean }>`
  border: 1px solid ${props => props.selected ? '#ff3b30' : props.theme.colors.border};
  background: ${props => props.selected ? 'rgba(255, 59, 48, 0.08)' : 'transparent'};
  border-radius: 6px;
  padding: 0.75rem;
  margin-top: 0.5rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s;

  &:hover {
    border-color: #ff3b30;
  }
`;

const PhoneNumber = styled.p`
  font-weight: 500;
  color: ${props => props.theme.colors.textPrimary};
`;

const PhoneName = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-top: 0.25rem;
`;

const QualityBadge = styled.span<{ rating: string }>`
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  background: ${props => {
    if (props.rating === 'GREEN') return 'rgba(52, 199, 89, 0.2)';
    if (props.rating === 'YELLOW') return 'rgba(255, 204, 0, 0.2)';
    return 'rgba(142, 142, 147, 0.2)';
  }};
  color: ${props => {
    if (props.rating === 'GREEN') return '#34c759';
    if (props.rating === 'YELLOW') return '#ffcc00';
    return '#8e8e93';
  }};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${props => props.variant === 'primary' ? `
    background: #ff3b30;
    color: white;

    &:hover:not(:disabled) {
      background: #ff6259;
      transform: translateY(-1px);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  ` : `
    background: transparent;
    color: ${props.theme.colors.textPrimary};
    border: 1px solid ${props.theme.colors.border};

    &:hover:not(:disabled) {
      background: ${props.theme.colors.backgroundTertiary};
    }
  `}
`;

const LoadingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.colors.background};
`;

const LoadingCard = styled.div`
  max-width: 500px;
  width: 100%;
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  padding: 3rem 2rem;
  text-align: center;
`;

const Spinner = styled.div`
  width: 64px;
  height: 64px;
  border: 4px solid ${props => props.theme.colors.border};
  border-top-color: #ff3b30;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 2rem;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 0.5rem;
`;

const LoadingText = styled.p`
  color: ${props => props.theme.colors.textSecondary};
`;

const WhatsAppCallback = () => {
  const { userId, user, loading: userLoading } = useCurrentUserId();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('Verificando autenticação...');
  const [error, setError] = useState<string | null>(null);
  const [wabas, setWabas] = useState<Waba[]>([]);
  const [showSelection, setShowSelection] = useState(false);
  const [selectedWaba, setSelectedWaba] = useState<Waba | null>(null);
  const [selectedPhone, setSelectedPhone] = useState<PhoneNumber | null>(null);
  const [accessToken, setAccessToken] = useState<string>('');
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const processCallback = async () => {
      if (userLoading) {
        setStatus('Restaurando sessão...');
        return;
      }

      if (!userId || !user) {
        setStatus('Aguardando autenticação...');
        timeoutId = setTimeout(() => {
          setError('Sessão expirou. Por favor, faça login novamente.');
          setTimeout(() => navigate('/', { replace: true }), 2000);
        }, 5000);
        return;
      }

      if (timeoutId) clearTimeout(timeoutId);

      const codeParam = searchParams.get('code');
      const stateParam = searchParams.get('state');

      if (!codeParam || !stateParam) {
        setError('Código de autorização não encontrado.');
        setTimeout(() => navigate('/whatsapp', { replace: true }), 3000);
        return;
      }

      try {
        setStatus('Carregando suas contas WhatsApp...');
        const response = await whatsappService.handleOAuthCallback({ code: codeParam, state: stateParam });

        if (response.success && response.wabas) {
          setWabas(response.wabas);
          setAccessToken(response.accessToken);
          setShowSelection(true);
        } else {
          setError(response.message || 'Erro ao processar autenticação');
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Erro ao processar autenticação');
      }
    };

    processCallback();
    return () => { if (timeoutId) clearTimeout(timeoutId); };
  }, [searchParams, navigate, userId, user, userLoading]);

  const completeConnection = async () => {
    if (!userId || !selectedWaba || !selectedPhone) return;

    setCompleting(true);
    try {
      const response = await whatsappService.completeOAuthConnection({
        googleId: userId,
        accessToken,
        wabaId: selectedWaba.id,
        phoneNumberId: selectedPhone.id
      });

      if (response.success) {
        setStatus('WhatsApp conectado com sucesso!');
        toast.success('🎉 WhatsApp conectado com sucesso!', {
          duration: 5000,
          style: {
            background: '#1a1a1a',
            color: '#fff',
            border: '1px solid #34c759',
          },
          iconTheme: {
            primary: '#34c759',
            secondary: '#fff',
          },
        });
        setTimeout(() => navigate('/whatsapp', { replace: true }), 1500);
      } else {
        const errorMsg = response.message || 'Erro ao completar conexão';
        setError(errorMsg);
        toast.error(errorMsg);
        setCompleting(false);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Erro ao completar conexão';
      setError(errorMsg);
      toast.error(errorMsg);
      setCompleting(false);
    }
  };

  if (showSelection && wabas.length > 0) {
    const hasAnyPhoneNumbers = wabas.some(w => w.hasPhoneNumbers);

    return (
      <Container>
        <Card>
          <Title>Escolha sua conta WhatsApp</Title>
          <Subtitle>Selecione a conta e o número que deseja conectar ao Loopra</Subtitle>

          {error && (
            <ErrorBox>
              <ErrorText>{error}</ErrorText>
            </ErrorBox>
          )}

          {wabas.map((waba) => (
            <WabaCard
              key={waba.id}
              selected={selectedWaba?.id === waba.id}
              onClick={() => {
                setSelectedWaba(waba);
                if (waba.phoneNumbers?.length > 0) {
                  setSelectedPhone(waba.phoneNumbers[0]);
                }
              }}
            >
              <WabaHeader>
                <div>
                  <WabaName>{waba.name}</WabaName>
                  <WabaId>ID: {waba.id}</WabaId>
                </div>
                <Badge variant={waba.hasPhoneNumbers ? 'success' : 'warning'}>
                  {waba.hasPhoneNumbers
                    ? `${waba.phoneNumbers.length} número${waba.phoneNumbers.length > 1 ? 's' : ''}`
                    : 'Sem números'}
                </Badge>
              </WabaHeader>

              {waba.hasPhoneNumbers && waba.phoneNumbers.length > 0 && selectedWaba?.id === waba.id && (
                <div style={{ marginTop: '1rem' }}>
                  <p style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', color: '#fff' }}>
                    Selecione o número:
                  </p>
                  {waba.phoneNumbers.map((phone) => (
                    <PhoneCard
                      key={phone.id}
                      selected={selectedPhone?.id === phone.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPhone(phone);
                      }}
                    >
                      <div>
                        <PhoneNumber>{phone.displayPhoneNumber}</PhoneNumber>
                        <PhoneName>{phone.verifiedName}</PhoneName>
                      </div>
                      <QualityBadge rating={phone.qualityRating}>
                        {phone.qualityRating}
                      </QualityBadge>
                    </PhoneCard>
                  ))}
                </div>
              )}
            </WabaCard>
          ))}

          {hasAnyPhoneNumbers && (
            <ButtonRow>
              <Button variant="secondary" onClick={() => navigate('/whatsapp', { replace: true })}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={completeConnection}
                disabled={!selectedWaba || !selectedPhone || completing}
              >
                {completing ? 'Conectando...' : 'Conectar'}
              </Button>
            </ButtonRow>
          )}
        </Card>
      </Container>
    );
  }

  return (
    <LoadingContainer>
      <LoadingCard>
        <Spinner />
        <LoadingTitle>{error ? 'Erro na Conexão' : 'Conectando WhatsApp'}</LoadingTitle>
        <LoadingText>{status}</LoadingText>
        {error && (
          <ErrorBox style={{ marginTop: '1.5rem' }}>
            <ErrorText>{error}</ErrorText>
          </ErrorBox>
        )}
      </LoadingCard>
    </LoadingContainer>
  );
};

export default WhatsAppCallback;
